/**
 * Theme subsystem contract (见 13 / §5.10): the platform-free types and
 * constants that define the design-token surface and the cross-process theme
 * surface. Kept separate from the concrete built-in palettes (`theme.ts`, which
 * authors the colors as JSON data) so this contract can be imported anywhere
 * without pulling palette data, and so editing colors never touches the contract.
 */

/**
 * The base color scheme of a theme. A theme is a single complete token set
 * tagged with its `type` (VSCode-style: a theme is either light or dark, never
 * both); the `system` preference resolves to one of the two built-in themes
 * based on the OS color scheme.
 */
export type ThemeType = 'light' | 'dark';

/**
 * What color scheme the user wants. `light`/`dark` pin a built-in theme;
 * `system` follows the OS (resolved against the main process's `nativeTheme`).
 */
export type ThemePreference = ThemeType | 'system';

/** Default preference for a fresh install: follow the OS color scheme. */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'system';

/** Type guard for a persisted / wire {@link ThemePreference}. */
export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * The fixed accent-color presets the user can pick (见 13 / §5.10). `brand` is
 * the product's own brand color; the others reuse the palette's `extended-*`
 * hues. A closed set on purpose: no custom hex, no contribution point.
 */
export const THEME_ACCENTS = [
  'brand',
  'red',
  'orange',
  'yellow',
  'green',
  'mint',
  'teal',
  'cyan',
  'blue',
] as const;

/** One of the {@link THEME_ACCENTS} presets. */
export type ThemeAccent = (typeof THEME_ACCENTS)[number];

/** Default accent for a fresh install: the product brand color. */
export const DEFAULT_THEME_ACCENT: ThemeAccent = 'brand';

/** Type guard for a persisted / wire {@link ThemeAccent}. */
export function isThemeAccent(value: unknown): value is ThemeAccent {
  return (
    typeof value === 'string' &&
    (THEME_ACCENTS as readonly string[]).includes(value)
  );
}

/**
 * A theme's semantic design tokens (见 13 / §5.10): a map of token name → value.
 * Consumers apply each as a CSS custom property (`--app-<name>`) on the document
 * root and reference it via `var(--app-<name>)`. Values are opaque strings
 * (color, shadow, …). Token names are an open string set — the palettes are
 * authored as data (`theme.ts`'s JSON sources), not as a closed TS union.
 *
 * In the two-tier model most color tokens are not raw values but references to a
 * base color (`var(--app-base-<name>)`), resolved against the
 * {@link ThemeBaseColors} applied alongside them. A few non-color tokens
 * (overlay, shadows) stay literal (composite/alpha values).
 */
export type ThemeTokens = Record<string, string>;

/**
 * A theme's base (primitive) color palette (见 13 / §5.10): a map of primitive
 * name → value — the raw Figma colors the semantic {@link ThemeTokens} reference
 * via `var(--app-base-<name>)`. Applied as `--app-base-<name>` custom
 * properties. An open string set authored as data (the Figma export drives the
 * concrete names/values in `theme.ts`'s JSON sources), not a closed TS union.
 */
export type ThemeBaseColors = Record<string, string>;

/** CSS custom-property prefix applied tokens are namespaced under (`--app-`). */
export const THEME_TOKEN_PREFIX = '--app-';

/** CSS custom-property prefix the base palette is namespaced under (`--app-base-`). */
export const THEME_BASE_PREFIX = '--app-base-';

/**
 * A complete, named theme (见 13): an id, its base {@link ThemeType}, the
 * primitive {@link ThemeBaseColors}, and the semantic {@link ThemeTokens} that
 * reference them.
 */
export interface ThemeDefinition {
  id: string;
  type: ThemeType;
  base: ThemeBaseColors;
  tokens: ThemeTokens;
}

export const BUILTIN_THEME_LIGHT_ID = 'hibro-light';
export const BUILTIN_THEME_DARK_ID = 'hibro-dark';

/**
 * Cross-process contract for the theme service (见 13 / §5.10). Lives
 * single-instance in the extension host (like `IConfigurationService`) and is
 * consumed by trusted renderers via an auto RPC proxy. Preference changes
 * broadcast on the EventBus as {@link THEME_CHANGED_EVENT} so every window — and
 * every sandboxed surface — repaints with one shared palette (§4.3).
 */
export const THEME_CHANNEL = 'theme';

/** EventBus event emitted when the effective theme changes (见 13 / §4.3). */
export const THEME_CHANGED_EVENT = 'platform.theme.changed';

/**
 * Payload of {@link THEME_CHANGED_EVENT}: the new preference + accent and the
 * resolved theme (which already carries the accent-derived palette).
 */
export interface ThemeChange {
  preference: ThemePreference;
  accent: ThemeAccent;
  theme: ThemeDefinition;
}

export interface IThemeService {
  /**
   * The current effective theme (preference resolved against the OS scheme,
   * with the accent applied to its palette).
   */
  getTheme(): Promise<ThemeDefinition>;
  /** The current user preference (`light`/`dark`/`system`). */
  getPreference(): Promise<ThemePreference>;
  /** Set + persist the preference, re-resolve, and broadcast the change. */
  setPreference(preference: ThemePreference): Promise<void>;
  /** The current accent preset. */
  getAccent(): Promise<ThemeAccent>;
  /** Set + persist the accent preset, re-resolve, and broadcast the change. */
  setAccent(accent: ThemeAccent): Promise<void>;
}
